import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { Spinner, Toast, css, PageHeader, Modal, Label } from '../components/UI';

// Catálogo de direcciones de entrega de las órdenes de compra. Lo editan el admin
// y el jefe de compras; el comprador elige de aquí al emitir una orden. La orden
// guarda una copia, así que editar o desactivar una dirección no cambia las ya emitidas.

const VACIA = { nombre: '', domicilio: '', horario: '', contacto: '', telefono: '', sucursal: '' };

export default function DireccionesEntrega() {
  const [items, setItems] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [error, setError] = useState(null);
  const [verInactivas, setVerInactivas] = useState(false);
  const [modal, setModal] = useState(null); // { id?, form, enviando }
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setError(null);
    try { setItems(await api.get('/direcciones-entrega?todas=1')); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/sucursales').then(s => setSucursales(s.map(x => x.nombre))).catch(() => {}); }, []);

  const abrir = (d) => setModal({ id: d?._id, form: d ? { ...VACIA, ...Object.fromEntries(Object.keys(VACIA).map(k => [k, d[k] || ''])) } : { ...VACIA }, enviando: false });
  const set = (k, v) => setModal(m => ({ ...m, form: { ...m.form, [k]: v } }));
  const guardar = async () => {
    const f = modal.form;
    if (!f.nombre.trim() || !f.domicilio.trim()) { toast_('El nombre y el domicilio son obligatorios', 'error'); return; }
    setModal(m => ({ ...m, enviando: true }));
    try {
      if (modal.id) await api.put(`/direcciones-entrega/${modal.id}`, f); else await api.post('/direcciones-entrega', f);
      toast_(modal.id ? 'Dirección actualizada' : 'Dirección agregada');
      setModal(null); load();
    } catch (e) { toast_(e.message, 'error'); setModal(m => ({ ...m, enviando: false })); }
  };
  const alternar = async (d) => {
    try { await api.put(`/direcciones-entrega/${d._id}`, { activa: !d.activa }); toast_(d.activa ? 'Dirección desactivada' : 'Dirección activada'); load(); }
    catch (e) { toast_(e.message, 'error'); }
  };

  const visibles = (items || []).filter(d => verInactivas || d.activa);
  const inactivas = (items || []).filter(d => !d.activa).length;
  const td = { padding: '10px 14px', verticalAlign: 'top' };

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <PageHeader title="Direcciones de entrega"
        subtitle="Las que se eligen al generar una orden de compra. Si ligas una sucursal, se propone sola en las requisiciones de esa sucursal."
        actions={<button type="button" onClick={() => abrir(null)} style={{ ...css.btn('#ff541c'), fontSize: 13 }}>Nueva dirección</button>} />

      {error && (
        <div role="alert" style={{ ...css.card, padding: 16, color: 'var(--danger)', display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
          <span>No se pudieron cargar las direcciones: {error}</span>
          <button type="button" onClick={load} style={css.btnGhost}>Reintentar</button>
        </div>
      )}
      {items === null && !error && <Spinner />}

      {items && (
        <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
          {visibles.length ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr>{['Nombre', 'Domicilio', 'Horario', 'Recibe', 'Sucursal', ''].map((h, i) => <th key={i} scope="col" style={{ textAlign: 'left', padding: '9px 14px', whiteSpace: 'nowrap' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {visibles.map(d => (
                    <tr key={d._id} style={{ borderTop: '1px solid var(--mesh-soft)', opacity: d.activa ? 1 : 0.55 }}>
                      <td style={{ ...td, fontWeight: 600 }}>{d.nombre}{!d.activa && <div style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--glass)' }}>Desactivada</div>}</td>
                      <td style={{ ...td, maxWidth: 340 }}>{d.domicilio}</td>
                      <td style={{ ...td, color: 'var(--ink-soft)' }}>{d.horario || '—'}</td>
                      <td style={{ ...td, color: 'var(--ink-soft)' }}>{[d.contacto, d.telefono].filter(Boolean).join(' · ') || '—'}</td>
                      <td style={{ ...td, color: 'var(--ink-soft)' }}>{d.sucursal || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button type="button" onClick={() => abrir(d)} style={{ ...css.btnGhost, fontSize: 12, padding: '4px 10px', marginRight: 6 }}>Editar</button>
                        <button type="button" onClick={() => alternar(d)} style={{ ...css.btnGhost, fontSize: 12, padding: '4px 10px' }}>{d.activa ? 'Desactivar' : 'Activar'}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: '28px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, marginBottom: 6 }}>Todavía no hay direcciones de entrega.</div>
              <div style={{ fontSize: 13, color: 'var(--glass)', marginBottom: 14 }}>Da de alta los almacenes, CEDIS y oficinas donde reciben mercancía.</div>
              <button type="button" onClick={() => abrir(null)} style={css.btn('#ff541c')}>Agregar la primera</button>
            </div>
          )}
          {inactivas > 0 && (
            <div style={{ padding: '10px 14px', borderTop: '1px solid var(--mesh-soft)' }}>
              <button type="button" onClick={() => setVerInactivas(v => !v)} style={{ ...css.btnGhost, fontSize: 12 }}>{verInactivas ? 'Ocultar desactivadas' : `Ver desactivadas (${inactivas})`}</button>
            </div>
          )}
        </div>
      )}

      <Modal open={!!modal} onClose={() => !modal?.enviando && setModal(null)} title={modal?.id ? 'Editar dirección' : 'Nueva dirección de entrega'} width={540}>
        {modal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div><Label required>Nombre</Label><input autoFocus value={modal.form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Ej. CEDIS Tepotzotlán IV" style={css.input(!modal.form.nombre.trim())} /></div>
            <div><Label required>Domicilio completo</Label><textarea rows={3} value={modal.form.domicilio} onChange={e => set('domicilio', e.target.value)} placeholder="Calle, número, colonia, código postal, municipio y estado" style={{ ...css.input(!modal.form.domicilio.trim()), resize: 'vertical' }} /></div>
            <div><Label>Horario de recepción</Label><input value={modal.form.horario} onChange={e => set('horario', e.target.value)} placeholder="Ej. Lunes a viernes 8:00 a 17:00" style={css.input(false)} /></div>
            <div className="ui-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><Label>Contacto</Label><input value={modal.form.contacto} onChange={e => set('contacto', e.target.value)} placeholder="Quién recibe" style={css.input(false)} /></div>
              <div><Label>Teléfono</Label><input value={modal.form.telefono} onChange={e => set('telefono', e.target.value)} inputMode="tel" style={css.input(false)} /></div>
            </div>
            <div>
              <Label>Sucursal ligada</Label>
              <select value={modal.form.sucursal} onChange={e => set('sucursal', e.target.value)} style={css.select(false)}>
                <option value="">Ninguna</option>
                {sucursales.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>Se propondrá sola al generar la orden de una requisición de esta sucursal.</div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setModal(null)} disabled={modal.enviando} style={css.btnGhost}>Cancelar</button>
              <button type="button" onClick={guardar} disabled={modal.enviando} style={{ ...css.btn('#ff541c'), opacity: modal.enviando ? 0.7 : 1 }}>{modal.enviando ? 'Guardando…' : 'Guardar'}</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
